/**
 * PeriodizationViewer Component
 *
 * Visualizes periodization plans including macrocycles and mesocycles.
 * Shows phase progression, volume landmarks, intensity zones, and current position.
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Minus,
  Zap,
  Dumbbell,
  Battery,
  Target,
  Info,
  Clock,
  Layers,
} from 'lucide-react';
import { format, differenceInWeeks, addWeeks, isWithinInterval, parseISO } from 'date-fns';

// Phase type configurations
const PHASE_CONFIGS: Record<
  string,
  {
    label: string;
    color: string;
    bgColor: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
  }
> = {
  accumulation: {
    label: 'Accumulation',
    color: 'text-chart-2',
    bgColor: 'bg-chart-2/15',
    description: 'High volume, moderate intensity - building work capacity',
    icon: TrendingUp,
  },
  intensification: {
    label: 'Intensification',
    color: 'text-chart-7',
    bgColor: 'bg-chart-7/15',
    description: 'Moderate volume, high intensity - building strength',
    icon: Zap,
  },
  realization: {
    label: 'Realization',
    color: 'text-chart-4',
    bgColor: 'bg-chart-4/15',
    description: 'Low volume, very high intensity - peaking performance',
    icon: Target,
  },
  deload: {
    label: 'Deload',
    color: 'text-chart-8',
    bgColor: 'bg-chart-8/15',
    description: 'Reduced volume and intensity - recovery week',
    icon: Battery,
  },
  hypertrophy: {
    label: 'Hypertrophy',
    color: 'text-chart-2',
    bgColor: 'bg-chart-2/15',
    description: 'Moderate weight, higher reps - muscle growth focus',
    icon: Dumbbell,
  },
  strength: {
    label: 'Strength',
    color: 'text-chart-5',
    bgColor: 'bg-chart-5/15',
    description: 'Heavy weight, lower reps - maximal strength',
    icon: TrendingUp,
  },
  power: {
    label: 'Power',
    color: 'text-chart-3',
    bgColor: 'bg-chart-3/15',
    description: 'Explosive movements - speed and power development',
    icon: Zap,
  },
  peaking: {
    label: 'Peaking',
    color: 'text-chart-5',
    bgColor: 'bg-chart-5/15',
    description: 'Competition preparation - maximal performance',
    icon: Target,
  },
  transition: {
    label: 'Transition',
    color: 'text-muted-foreground',
    bgColor: 'bg-muted',
    description: 'Active recovery between training cycles',
    icon: Minus,
  },
};

// Volume landmark configurations
const VOLUME_LANDMARKS: Record<string, { label: string; description: string; color: string }> = {
  MEV: {
    label: 'MEV',
    description: 'Minimum Effective Volume - minimum needed to progress',
    color: 'text-success',
  },
  MAV: {
    label: 'MAV',
    description: 'Maximum Adaptive Volume - optimal for most adaptations',
    color: 'text-info',
  },
  MRV: {
    label: 'MRV',
    description: 'Maximum Recoverable Volume - upper limit before overreaching',
    color: 'text-destructive',
  },
};

// Intensity zone configurations
const INTENSITY_ZONES: Record<string, { label: string; rpeRange: string; color: string }> = {
  low: { label: 'Low', rpeRange: 'RPE 5-6', color: 'bg-success' },
  moderate: { label: 'Moderate', rpeRange: 'RPE 6-7', color: 'bg-warning' },
  high: { label: 'High', rpeRange: 'RPE 7-8', color: 'bg-warning/80' },
  very_high: { label: 'Very High', rpeRange: 'RPE 8-10', color: 'bg-destructive' },
};

export interface Mesocycle {
  id: string;
  week_start: number;
  week_end: number;
  name: string;
  phase_type: string;
  volume_landmark: string | null;
  intensity_zone: string | null;
  rpe_range_low: number | null;
  rpe_range_high: number | null;
  rep_ranges: Record<string, { min: number; max: number }> | null;
  training_days_per_week: number | null;
}

export interface PeriodizationPlan {
  id: string;
  name: string;
  goal: string;
  start_date: string;
  end_date: string;
  total_weeks: number;
  phases: Array<{
    name: string;
    weeks: number;
    focus: string;
    volume_level: string;
    intensity_level: string;
  }>;
  status: 'draft' | 'active' | 'paused' | 'completed';
  current_week: number;
  mesocycles?: Mesocycle[];
}

interface PeriodizationViewerProps {
  plan: PeriodizationPlan;
  showDetails?: boolean;
  compact?: boolean;
  onMesocycleClick?: (mesocycle: Mesocycle) => void;
}

export default function PeriodizationViewer({
  plan,
  showDetails = true,
  compact = false,
  onMesocycleClick,
}: PeriodizationViewerProps) {
  const [expandedMesocycles, setExpandedMesocycles] = useState<string[]>([]);

  const startDate = parseISO(plan.start_date);
  const endDate = parseISO(plan.end_date);
  const today = new Date();

  // Calculate current week
  const currentWeek = Math.max(1, Math.min(plan.total_weeks, differenceInWeeks(today, startDate) + 1));
  const progressPercent = (currentWeek / plan.total_weeks) * 100;

  // Check if we're in a specific mesocycle
  const getCurrentMesocycle = () => {
    return plan.mesocycles?.find(
      (m) => currentWeek >= m.week_start && currentWeek <= m.week_end
    );
  };

  const currentMesocycle = getCurrentMesocycle();

  const toggleMesocycle = (id: string) => {
    setExpandedMesocycles((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  // Status badge variant
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'default';
      case 'completed':
        return 'secondary';
      case 'paused':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  if (compact) {
    return (
      <Card>
        <CardContent className="pt-4">
          <div className="space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">{plan.name}</h3>
                <p className="text-xs text-muted-foreground">
                  {format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')}
                </p>
              </div>
              <Badge variant={getStatusVariant(plan.status)}>{plan.status}</Badge>
            </div>

            {/* Progress */}
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span>Week {currentWeek} of {plan.total_weeks}</span>
                <span>{Math.round(progressPercent)}%</span>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </div>

            {/* Current Phase */}
            {currentMesocycle && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Current:</span>
                <Badge
                  className={cn(
                    PHASE_CONFIGS[currentMesocycle.phase_type]?.bgColor,
                    PHASE_CONFIGS[currentMesocycle.phase_type]?.color,
                    'border-0'
                  )}
                >
                  {PHASE_CONFIGS[currentMesocycle.phase_type]?.label || currentMesocycle.phase_type}
                </Badge>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Plan Overview Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {plan.name}
              </CardTitle>
              <CardDescription>
                {format(startDate, 'MMMM d, yyyy')} - {format(endDate, 'MMMM d, yyyy')}
              </CardDescription>
            </div>
            <Badge variant={getStatusVariant(plan.status)} className="capitalize">
              {plan.status}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Progress Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Progress</span>
              <span className="text-sm text-muted-foreground">
                Week {currentWeek} of {plan.total_weeks}
              </span>
            </div>
            <Progress value={progressPercent} className="h-3" />
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Goal</span>
              <p className="font-medium capitalize">{plan.goal.replace(/_/g, ' ')}</p>
            </div>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Duration</span>
              <p className="font-medium">{plan.total_weeks} weeks</p>
            </div>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Phases</span>
              <p className="font-medium">{plan.mesocycles?.length || plan.phases.length} phases</p>
            </div>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Current Week</span>
              <p className="font-medium">{currentWeek}</p>
            </div>
          </div>

          {/* Current Mesocycle Highlight */}
          {currentMesocycle && (
            <>
              <Separator />
              <div className="space-y-3">
                <h4 className="text-sm font-medium">Current Phase</h4>
                <Card
                  className={cn(
                    'border-2',
                    PHASE_CONFIGS[currentMesocycle.phase_type]?.bgColor?.replace('bg-', 'border-').replace('100', '300')
                  )}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          {PHASE_CONFIGS[currentMesocycle.phase_type]?.icon && (
                            <div
                              className={cn(
                                'p-2 rounded-lg',
                                PHASE_CONFIGS[currentMesocycle.phase_type]?.bgColor
                              )}
                            >
                              {(() => {
                                const Icon = PHASE_CONFIGS[currentMesocycle.phase_type]?.icon;
                                return Icon ? (
                                  <Icon
                                    className={cn(
                                      'h-5 w-5',
                                      PHASE_CONFIGS[currentMesocycle.phase_type]?.color
                                    )}
                                  />
                                ) : null;
                              })()}
                            </div>
                          )}
                          <div>
                            <h3 className="font-semibold">{currentMesocycle.name}</h3>
                            <p className="text-xs text-muted-foreground">
                              Weeks {currentMesocycle.week_start} - {currentMesocycle.week_end}
                            </p>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {PHASE_CONFIGS[currentMesocycle.phase_type]?.description}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {currentMesocycle.volume_landmark && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge variant="outline" className="gap-1">
                                  <Layers className="h-3 w-3" />
                                  {currentMesocycle.volume_landmark}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  {VOLUME_LANDMARKS[currentMesocycle.volume_landmark]?.description}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {currentMesocycle.intensity_zone && (
                          <Badge
                            className={cn(
                              'gap-1',
                              INTENSITY_ZONES[currentMesocycle.intensity_zone]?.color,
                              'text-white'
                            )}
                          >
                            {INTENSITY_ZONES[currentMesocycle.intensity_zone]?.rpeRange}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Timeline Visualization */}
      {showDetails && plan.mesocycles && plan.mesocycles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Phase Timeline</CardTitle>
            <CardDescription>Visual overview of all training phases</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Visual Timeline Bar */}
            <div className="space-y-4">
              <div className="relative h-12 rounded-lg overflow-hidden flex">
                {plan.mesocycles.map((mesocycle, index) => {
                  const widthPercent =
                    ((mesocycle.week_end - mesocycle.week_start + 1) / plan.total_weeks) * 100;
                  const isCurrent =
                    currentWeek >= mesocycle.week_start && currentWeek <= mesocycle.week_end;
                  const config = PHASE_CONFIGS[mesocycle.phase_type];

                  return (
                    <TooltipProvider key={mesocycle.id}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={cn(
                              'h-full flex items-center justify-center cursor-pointer transition-all',
                              config?.bgColor || 'bg-muted',
                              isCurrent && 'ring-2 ring-primary ring-offset-1'
                            )}
                            style={{ width: `${widthPercent}%` }}
                            onClick={() => onMesocycleClick?.(mesocycle)}
                          >
                            {widthPercent > 10 && (
                              <span
                                className={cn(
                                  'text-xs font-medium truncate px-1',
                                  config?.color || 'text-muted-foreground'
                                )}
                              >
                                {config?.label || mesocycle.phase_type}
                              </span>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="space-y-1">
                            <p className="font-medium">{mesocycle.name}</p>
                            <p className="text-xs">
                              Weeks {mesocycle.week_start} - {mesocycle.week_end}
                            </p>
                            <p className="text-xs text-muted-foreground">{config?.description}</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}

                {/* Current Week Indicator */}
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-primary z-10"
                  style={{ left: `${progressPercent}%` }}
                >
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-primary rounded-full" />
                </div>
              </div>

              {/* Week Labels */}
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Week 1</span>
                <span>Week {Math.ceil(plan.total_weeks / 2)}</span>
                <span>Week {plan.total_weeks}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Mesocycle List */}
      {showDetails && plan.mesocycles && plan.mesocycles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Phase Details</CardTitle>
            <CardDescription>Detailed breakdown of each training phase</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {plan.mesocycles.map((mesocycle) => {
              const config = PHASE_CONFIGS[mesocycle.phase_type];
              const isCurrent =
                currentWeek >= mesocycle.week_start && currentWeek <= mesocycle.week_end;
              const isPast = currentWeek > mesocycle.week_end;
              const isExpanded = expandedMesocycles.includes(mesocycle.id);

              return (
                <Collapsible
                  key={mesocycle.id}
                  open={isExpanded}
                  onOpenChange={() => toggleMesocycle(mesocycle.id)}
                >
                  <Card
                    className={cn(
                      'transition-colors',
                      isCurrent && 'border-primary',
                      isPast && 'opacity-60'
                    )}
                  >
                    <CollapsibleTrigger asChild>
                      <CardContent className="pt-4 cursor-pointer hover:bg-muted/30">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                            <div
                              className={cn('p-2 rounded-lg', config?.bgColor || 'bg-muted')}
                            >
                              {config?.icon && (
                                <config.icon
                                  className={cn('h-4 w-4', config?.color || 'text-muted-foreground')}
                                />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{mesocycle.name}</span>
                                {isCurrent && (
                                  <Badge variant="default" className="text-xs">
                                    Current
                                  </Badge>
                                )}
                                {isPast && (
                                  <Badge variant="secondary" className="text-xs">
                                    Completed
                                  </Badge>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground">
                                Weeks {mesocycle.week_start} - {mesocycle.week_end} (
                                {mesocycle.week_end - mesocycle.week_start + 1} weeks)
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {mesocycle.volume_landmark && (
                              <Badge variant="outline" className="text-xs">
                                {mesocycle.volume_landmark}
                              </Badge>
                            )}
                            {mesocycle.intensity_zone && (
                              <Badge
                                className={cn(
                                  'text-xs',
                                  INTENSITY_ZONES[mesocycle.intensity_zone]?.color,
                                  'text-white'
                                )}
                              >
                                {INTENSITY_ZONES[mesocycle.intensity_zone]?.label}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0 pb-4">
                        <Separator className="my-4" />
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-xs text-muted-foreground block">Phase Type</span>
                            <span className={cn('font-medium', config?.color)}>
                              {config?.label || mesocycle.phase_type}
                            </span>
                          </div>
                          {mesocycle.volume_landmark && (
                            <div>
                              <span className="text-xs text-muted-foreground block">
                                Volume Target
                              </span>
                              <span className="font-medium">
                                {VOLUME_LANDMARKS[mesocycle.volume_landmark]?.label} -{' '}
                                {VOLUME_LANDMARKS[mesocycle.volume_landmark]?.description}
                              </span>
                            </div>
                          )}
                          {mesocycle.rpe_range_low && mesocycle.rpe_range_high && (
                            <div>
                              <span className="text-xs text-muted-foreground block">RPE Range</span>
                              <span className="font-medium">
                                {mesocycle.rpe_range_low} - {mesocycle.rpe_range_high}
                              </span>
                            </div>
                          )}
                          {mesocycle.training_days_per_week && (
                            <div>
                              <span className="text-xs text-muted-foreground block">
                                Training Days
                              </span>
                              <span className="font-medium">
                                {mesocycle.training_days_per_week} days/week
                              </span>
                            </div>
                          )}
                        </div>
                        {mesocycle.rep_ranges && Object.keys(mesocycle.rep_ranges).length > 0 && (
                          <div className="mt-4">
                            <span className="text-xs text-muted-foreground block mb-2">
                              Rep Ranges
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(mesocycle.rep_ranges).map(([type, range]) => (
                                <Badge key={type} variant="secondary">
                                  {type}: {range.min}-{range.max}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        <p className="text-sm text-muted-foreground mt-4">{config?.description}</p>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      {showDetails && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Info className="h-4 w-4" />
              Legend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(PHASE_CONFIGS).map(([key, config]) => (
                <div key={key} className="flex items-center gap-2">
                  <div className={cn('w-4 h-4 rounded', config.bgColor)} />
                  <span className="text-sm">{config.label}</span>
                </div>
              ))}
            </div>
            <Separator className="my-4" />
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Volume Landmarks</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                {Object.entries(VOLUME_LANDMARKS).map(([key, config]) => (
                  <div key={key}>
                    <span className={cn('font-medium', config.color)}>{config.label}</span>
                    <p className="text-xs text-muted-foreground">{config.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
